import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Button,
  Grid,
  Select,
  Heading,
  IconButton,
  Box,
  VStack,
  Stack,
  FormErrorMessage,
  Text,
} from "@chakra-ui/react";
import { X } from "lucide-react";

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (product: {
    serialNumber: string;
    productName: string;
    initialQuantity: number;
    category: string;
    lotNumber: string;
    expirationDate: string;
    threshold: number;
    type: string;
  }) => void;
  editingProduct?: {
    serialNumber: string;
    productName: string;
    initialQuantity: number;
    category: string;
    lotNumber: string;
    expirationDate: string;
    threshold: number;
    type: string;
  } | null;
  categories?: string[];
  productTypes?: string[];
}

interface ErrorMessagesType {
  [key: string]: { message: string };
}

const AddProductModal: React.FC<AddProductModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingProduct = null,
  categories = [],
  productTypes = [],
}) => {
  // Form states
  const [serialNumber, setSerialNumber] = useState("");
  const [productName, setProductName] = useState("");
  const [initialQuantity, setInitialQuantity] = useState("");
  const [category, setCategory] = useState("");
  const [lotNumber, setLotNumber] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [threshold, setThreshold] = useState("");
  const [type, setType] = useState("");

  // Error messages state
  const [errorMessages, setErrorMessages] = useState<ErrorMessagesType>({});

  // Dummy ref to prevent auto focus
  const initialRef = useRef<HTMLDivElement>(null);

  // Pre-fill form when editing
  useEffect(() => {
    if (isOpen && editingProduct) {
      setSerialNumber(editingProduct.serialNumber || "");
      setProductName(editingProduct.productName || "");
      setInitialQuantity(editingProduct.initialQuantity.toString() || "");
      setCategory(editingProduct.category || "");
      setLotNumber(editingProduct.lotNumber || "");
      setExpirationDate(editingProduct.expirationDate || "");
      setThreshold(editingProduct.threshold.toString() || "");
      setType(editingProduct.type || "");
    } else if (!isOpen) {
      // Reset modal when closed
      resetForm();
    }
  }, [isOpen, editingProduct]);

  const resetForm = () => {
    setSerialNumber("");
    setProductName("");
    setInitialQuantity("");
    setCategory("");
    setLotNumber("");
    setExpirationDate("");
    setThreshold("");
    setType("");
    setErrorMessages({});
  };

  const validateForm = () => {
    const errors: ErrorMessagesType = {};

    if (!serialNumber.trim()) {
      errors.serialNumber = { message: "Serial number is required" };
    }
    if (!productName.trim()) {
      errors.productName = { message: "Product name is required" };
    }
    if (!initialQuantity || Number(initialQuantity) < 0) {
      errors.initialQuantity = {
        message: "Valid initial quantity is required",
      };
    }
    if (!category) {
      errors.category = { message: "Category is required" };
    }
    if (!lotNumber.trim()) {
      errors.lotNumber = { message: "Lot number is required" };
    }
    if (!expirationDate) {
      errors.expirationDate = { message: "Expiration date is required" };
    }
    if (!threshold || Number(threshold) < 0) {
      errors.threshold = { message: "Valid threshold is required" };
    }
    if (!type) {
      errors.type = { message: "Type is required" };
    }

    setErrorMessages(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    onSave({
      serialNumber,
      productName,
      initialQuantity: Number(initialQuantity),
      category,
      lotNumber,
      expirationDate,
      threshold: threshold ? Number(threshold) : 0,
      type,
    });

    onClose();
    resetForm();
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <>
      {isOpen && <div className="background-blur" />}

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        isCentered
        size={{ base: "full", sm: "md", md: "lg", lg: "xl" }}
        scrollBehavior="inside"
        initialFocusRef={initialRef}
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
        <ModalContent
          borderRadius={{ base: 0, md: "none" }}
          mx={{ base: 0, md: 4 }}
          maxH={{ base: "100vh", md: "90vh" }}
        >
          <ModalHeader
            fontSize={{ base: "lg", md: "xl" }}
            py={{ base: 3, md: 4 }}
            px={{ base: 4, md: 6 }}
            position="relative"
          >
            {editingProduct ? "Edit Product" : "Add New Product"}
            {/* Close Button */}
            <IconButton
              aria-label="Close Modal"
              icon={<X size={18} />}
              onClick={handleClose}
              position="absolute"
              right={{ base: 3, md: 5 }}
              top={{ base: 3, md: 5 }}
              size={{ base: "sm", md: "md" }}
              variant="link"
              color="#0A1B39"
              _hover={{ color: "#DC3611" }}
              _focus={{ boxShadow: "none" }}
            />
          </ModalHeader>

          <Box borderBottom="1px solid #E7E8EB" mx={{ base: 4, md: 6 }} />

          <ModalBody
            py={{ base: 3, md: 4 }}
            px={{ base: 4, md: 6 }}
            overflowY="auto"
          >
            {/* Dummy div for initial focus */}
            <div ref={initialRef} tabIndex={-1} />

            <Heading
              size={{ base: "xs", sm: "sm", md: "sm" }}
              mb={{ base: 3, md: 4 }}
              fontWeight="semibold"
              color="#0A1B39"
            >
              Product Information
            </Heading>

            <VStack spacing={{ base: 3, md: 4 }} align="stretch">
              {/* Mobile: Stack all fields */}
              <Box display={{ base: "block", sm: "none" }}>
                <VStack spacing={3} align="stretch">
                  {/* Serial Number */}
                  <FormControl
                    isRequired
                    isInvalid={!!errorMessages.serialNumber}
                  >
                    <FormLabel
                      fontSize="xs"
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Serial Number{" "}
                    </FormLabel>
                    <Input
                      placeholder="Enter serial number"
                      size="sm"
                      fontSize="xs"
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h="36px"
                      minH="36px"
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={serialNumber}
                      onChange={(e) => setSerialNumber(e.target.value)}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.serialNumber?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Product Name */}
                  <FormControl
                    isRequired
                    isInvalid={!!errorMessages.productName}
                  >
                    <FormLabel
                      fontSize="xs"
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Product Name{" "}
                    </FormLabel>
                    <Input
                      placeholder="Enter product name"
                      size="sm"
                      fontSize="xs"
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h="36px"
                      minH="36px"
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.productName?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Initial Quantity */}
                  <FormControl
                    isRequired
                    isInvalid={!!errorMessages.initialQuantity}
                  >
                    <FormLabel
                      fontSize="xs"
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Initial Quantity{" "}
                    </FormLabel>
                    <Input
                      placeholder="0"
                      type="number"
                      min={0}
                      size="sm"
                      fontSize="xs"
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h="36px"
                      minH="36px"
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={initialQuantity}
                      onChange={(e) => setInitialQuantity(e.target.value)}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.initialQuantity?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Category */}
                  <FormControl isRequired isInvalid={!!errorMessages.category}>
                    <FormLabel
                      fontSize="xs"
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Category{" "}
                    </FormLabel>
                    <Select
                      placeholder="Select category"
                      size="sm"
                      fontSize="xs"
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h="36px"
                      minH="36px"
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      {categories.map((cat, index) => (
                        <option key={index} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.category?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Lot Number */}
                  <FormControl isRequired isInvalid={!!errorMessages.lotNumber}>
                    <FormLabel
                      fontSize="xs"
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Lot Number{" "}
                    </FormLabel>
                    <Input
                      placeholder="Enter lot number"
                      size="sm"
                      fontSize="xs"
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h="36px"
                      minH="36px"
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={lotNumber}
                      onChange={(e) => setLotNumber(e.target.value)}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.lotNumber?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Expiration Date */}
                  <FormControl
                    isRequired
                    isInvalid={!!errorMessages.expirationDate}
                  >
                    <FormLabel
                      fontSize="xs"
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Expiration Date{" "}
                    </FormLabel>
                    <Input
                      type="date"
                      min={today}
                      size="sm"
                      fontSize="xs"
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h="36px"
                      minH="36px"
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={expirationDate}
                      onChange={(e) => setExpirationDate(e.target.value)}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.expirationDate?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Threshold */}
                  <FormControl isRequired isInvalid={!!errorMessages.threshold}>
                    <FormLabel
                      fontSize="xs"
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Threshold{" "}
                    </FormLabel>
                    <Input
                      placeholder="0"
                      type="number"
                      min={0}
                      size="sm"
                      fontSize="xs"
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h="36px"
                      minH="36px"
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={threshold}
                      onChange={(e) => setThreshold(e.target.value)}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.threshold?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Type */}
                  <FormControl isRequired isInvalid={!!errorMessages.type}>
                    <FormLabel
                      fontSize="xs"
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Type{" "}
                    </FormLabel>
                    <Select
                      placeholder="Select type"
                      size="sm"
                      fontSize="xs"
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h="36px"
                      minH="36px"
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                    >
                      {productTypes.map((productType, index) => (
                        <option key={index} value={productType}>
                          {productType}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.type?.message}
                    </FormErrorMessage>
                  </FormControl>
                </VStack>
              </Box>

              {/* Desktop/Tablet: layout */}
              <Grid
                templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                gap={{ base: 3, md: 4 }}
                display={{ base: "none", sm: "grid" }}
              >
                {/* Left Column */}
                <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                  <FormControl
                    isRequired
                    isInvalid={!!errorMessages.serialNumber}
                  >
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Serial Number{" "}
                    </FormLabel>
                    <Input
                      placeholder="Enter serial number"
                      size={{ base: "sm", md: "md" }}
                      fontSize={{ base: "xs", md: "sm" }}
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h={{ base: "36px", md: "40px" }}
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={serialNumber}
                      onChange={(e) => setSerialNumber(e.target.value)}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.serialNumber?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isRequired
                    isInvalid={!!errorMessages.productName}
                  >
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Product Name{" "}
                    </FormLabel>
                    <Input
                      placeholder="Enter product name"
                      size={{ base: "sm", md: "md" }}
                      fontSize={{ base: "xs", md: "sm" }}
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h={{ base: "36px", md: "40px" }}
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.productName?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isRequired
                    isInvalid={!!errorMessages.initialQuantity}
                  >
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Initial Quantity{" "}
                    </FormLabel>
                    <Input
                      placeholder="0"
                      type="number"
                      min={0}
                      size={{ base: "sm", md: "md" }}
                      fontSize={{ base: "xs", md: "sm" }}
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h={{ base: "36px", md: "40px" }}
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={initialQuantity}
                      onChange={(e) => setInitialQuantity(e.target.value)}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.initialQuantity?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errorMessages.category}>
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Category{" "}
                    </FormLabel>
                    <Select
                      placeholder="Select category"
                      size={{ base: "sm", md: "md" }}
                      fontSize={{ base: "xs", md: "sm" }}
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h={{ base: "36px", md: "40px" }}
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      {categories.map((cat, index) => (
                        <option key={index} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.category?.message}
                    </FormErrorMessage>
                  </FormControl>
                </VStack>

                {/* Right Column */}
                <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                  <FormControl isRequired isInvalid={!!errorMessages.lotNumber}>
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Lot Number{" "}
                    </FormLabel>
                    <Input
                      placeholder="Enter lot number"
                      size={{ base: "sm", md: "md" }}
                      fontSize={{ base: "xs", md: "sm" }}
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h={{ base: "36px", md: "40px" }}
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={lotNumber}
                      onChange={(e) => setLotNumber(e.target.value)}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.lotNumber?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isRequired
                    isInvalid={!!errorMessages.expirationDate}
                  >
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Expiration Date{" "}
                    </FormLabel>
                    <Input
                      type="date"
                      min={today}
                      size={{ base: "sm", md: "md" }}
                      fontSize={{ base: "xs", md: "sm" }}
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h={{ base: "36px", md: "40px" }}
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={expirationDate}
                      onChange={(e) => setExpirationDate(e.target.value)}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.expirationDate?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errorMessages.threshold}>
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Threshold{" "}
                    </FormLabel>
                    <Input
                      placeholder="0"
                      type="number"
                      min={0}
                      size={{ base: "sm", md: "md" }}
                      fontSize={{ base: "xs", md: "sm" }}
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h={{ base: "36px", md: "40px" }}
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={threshold}
                      onChange={(e) => setThreshold(e.target.value)}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.threshold?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errorMessages.type}>
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Type{" "}
                    </FormLabel>
                    <Select
                      placeholder="Select type"
                      size={{ base: "sm", md: "md" }}
                      fontSize={{ base: "xs", md: "sm" }}
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h={{ base: "36px", md: "40px" }}
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                    >
                      {productTypes.map((productType, index) => (
                        <option key={index} value={productType}>
                          {productType}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.type?.message}
                    </FormErrorMessage>
                  </FormControl>
                </VStack>
              </Grid>
            </VStack>
          </ModalBody>

          <Box borderBottom="1px solid #E7E8EB" mx={{ base: 4, md: 6 }} />

          <ModalFooter
            py={{ base: 3, md: 4 }}
            px={{ base: 4, md: 6 }}
            gap={{ base: 2, md: 3 }}
            flexWrap="wrap"
          >
            <Stack
              direction={{ base: "column", sm: "row" }}
              spacing={{ base: 2, md: 3 }}
              w="full"
              justify="flex-end"
            >
              <Button
                variant="outline"
                onClick={handleClose}
                w={{ base: "full", sm: "auto" }}
                size={{ base: "sm", md: "md" }}
              >
                Cancel
              </Button>
              <Button
                bg="#DC3611"
                color="white"
                _hover={{ bg: "#bf3c1fff" }}
                onClick={handleSave}
                w={{ base: "full", sm: "auto" }}
                size={{ base: "sm", md: "md" }}
              >
                {editingProduct ? "Save Changes" : "Add New Product"}
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddProductModal;
